import PropTypes from "prop-types";
import React, { createContext, useContext, useState } from "react";

import { useAppSetting, usePageId } from "@swa-ui/application";
import { AuthContext, useAuthenticatedPersistedState } from "@swa-ui/authentication";
import { getBootstrapData } from "@swa-ui/bootstrap";
import { isResponseOk, useFetch } from "@swa-ui/fetch";

import { useChaseStorage } from "../useChaseStorage";

const CHASE_ENDPOINT = "/api/chase/v2/chase";
const CHASE_OFFERS = "offers";
const DEFAULT_CHASE_TTL_IN_SECONDS = 86400;

export const ChaseContext = createContext();
export const ChaseProvider = (props) => {
  const { children } = props;
  const { isLoggedIn } = useContext(AuthContext);
  const { pageId } = usePageId();
  const { post, response } = useFetch(CHASE_ENDPOINT, {
    cachePolicy: "no-cache",
  });
  const [chaseDetails, setChaseDetails] = useAuthenticatedPersistedState({
    defaultValue: {},
    key: "ChaseProvider-chaseDetails",
  });
  const { getChaseStorage, setChaseStorage } = useChaseStorage();
  const [chaseInstantCreditDetails, setChaseInstantCreditDetails] = useState(
    getChaseStorage() ?? { returnUrl: null, chaseSessionId: null }
  );
  const mboxParameters = useAppSetting("mboxParameters", {});

  return <ChaseContext.Provider value={getContextValue()}>{children}</ChaseContext.Provider>;

  function updateChaseInstantCreditDetails(updatedDetails) {
    const newDetails = {
      ...chaseInstantCreditDetails,
      ...updatedDetails,
    };
    setChaseInstantCreditDetails(newDetails);
    setChaseStorage(newDetails);
  }

  function getContextValue() {
    return {
      chaseDetails,
      chaseInstantCreditDetails,
      getChaseContext,
      updateChaseInstantCreditDetails,
    };
  }

  async function getChaseContext() {
    let chaseContext = {};

    if (shouldFetchChaseOffers()) {
      const chaseOffers = await fetchChaseOffers();
      const { highValueIndicator, offerIdentifier, offers } = chaseOffers ?? {};

      if (offers?.length) {
        const acquisitionSourceCodes = offers
          ?.map((offer) => offer?.acquisitionSourceCode)
          ?.join(",");

        chaseContext = {
          acquisitionSourceCodes,
          highValueIndicator,
          lastUpdated: Date.now(),
          offerIdentifier,
        };

        setChaseDetails(chaseContext);
      }
    }

    return chaseContext;
  }

  function shouldFetchChaseOffers() {
    const { offerId, acquisitionSourceCodes, highValueIndicator } = mboxParameters;

    return isLoggedIn() && isStale() && (offerId || acquisitionSourceCodes || highValueIndicator);
  }

  function isStale() {
    const { lastUpdated } = chaseDetails ?? {};
    const chaseOfferTtlInSeconds = getTtlInSeconds("offers");

    return (
      typeof lastUpdated !== "number" || Date.now() - lastUpdated > chaseOfferTtlInSeconds * 1000
    );
  }

  async function fetchChaseOffers() {
    let chaseOffersResponse = {};

    await post(CHASE_OFFERS, { pageId });

    if (isResponseOk(response)) {
      chaseOffersResponse = response.data;
    }

    return chaseOffersResponse;
  }

  function getTtlInSeconds(key) {
    const chaseTtlConfig = getBootstrapData("chase-ttl-config") ?? [];

    return (
      chaseTtlConfig?.find((config) => config.type === key)?.ttlInSeconds ??
      DEFAULT_CHASE_TTL_IN_SECONDS
    );
  }
};

ChaseContext.displayName = "ChaseContext";
ChaseProvider.propTypes = {
  /** Content to be rendered on the page. */
  children: PropTypes.node.isRequired,
};
